import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast.jsx";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";

const LeadSourceCostForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(!!id);
    const [leadSourceOptions, setLeadSourceOptions] = useState([]);

    // Form state
    const [formData, setFormData] = useState({
        start_date: "",
        end_date: "",
        lead_source: "",
        cost: ""
    });

    const [errors, setErrors] = useState({});

    // Fetch lead source options and existing data for edit
    useEffect(() => {
        const fetchData = async () => {
            if (id) {
                try {
                    setFormLoading(true);
                    const token = localStorage.getItem("authToken");
                    const response = await axios.get(`${API_URL}lead-source-cost/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            accept: "application/json",
                        },
                    });

                    if (response.data) {
                        const costData = response.data.leadsourcecost;
                        setFormData({
                            start_date: costData.start_date || "",
                            end_date: costData.end_date || "",
                            lead_source: costData.lead_source || "",
                            cost: costData.cost || ""
                        });

                        if (response.data.lead_source_options) {
                            setLeadSourceOptions(response.data.lead_source_options);
                        }
                    }
                } catch (error) {
                    showToast.error("Failed to fetch lead source cost data.", "error");
                    console.error("Error fetching data:", error);
                } finally {
                    setFormLoading(false);
                }
            } else {
                // For add page, fetch lead source options
                fetchLeadSourceOptions();
            }
        };

        fetchData();
    }, [id]);

    const fetchLeadSourceOptions = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`${API_URL}lead-source-cost?page=0&limit=1`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    accept: "application/json",
                },
            });

            if (response.data && response.data.lead_source_options) {
                setLeadSourceOptions(response.data.lead_source_options);
            }
        } catch (error) {
            console.error("Error fetching lead source options:", error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.start_date.trim()) {
            newErrors.start_date = "Start Date is required";
        }

        if (!formData.end_date.trim()) {
            newErrors.end_date = "End Date is required";
        }

        if (!formData.lead_source.trim()) {
            newErrors.lead_source = "Lead Source is required";
        }

        if (!formData.cost) {
            newErrors.cost = "Cost is required";
        } else if (isNaN(formData.cost) || parseFloat(formData.cost) <= 0) {
            newErrors.cost = "Cost must be a valid positive number";
        }

        // Validate date range
        if (formData.start_date && formData.end_date) {
            const startDate = new Date(formData.start_date);
            const endDate = new Date(formData.end_date);

            if (endDate < startDate) {
                newErrors.end_date = "End Date cannot be before Start Date";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast.error("Please fix the errors in the form.", "error");
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");

            const payload = {
                start_date: formData.start_date,
                end_date: formData.end_date,
                lead_source: formData.lead_source,
                cost: parseFloat(formData.cost)
            };

            let response;
            if (id) {
                // Edit existing record
                response = await axios.post(`${API_URL}lead-source-cost/${id}`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        accept: "application/json",
                    },
                });
                showToast.success("Lead source cost updated successfully.", "success");
            } else {
                // Add new record
                response = await axios.post(`${API_URL}lead-source-cost`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        accept: "application/json",
                    },
                });
                showToast.success("Lead source cost added successfully.", "success");
            }

            // Redirect back to list page
            navigate("/Website/LeadSourceCost");

        } catch (error) {
            const errorMessage = id
                ? "Failed to update lead source cost."
                : "Failed to add lead source cost.";
            showToast.error(errorMessage, "error");
            console.error("Error saving data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate("/Website/LeadSourceCost");
    };

    const operation = id ? "Edit" : "Add";
    const managePage = `${operation} Lead Source Cost`;

    if (formLoading) {
        return (
            <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
            <div className="">
                <div className="panel panel-default with-rounded-corner with-shadow p-10">
                    <form onSubmit={handleSubmit} className="stdform">
                        <input type="hidden" name="id" value={id || ""} />

                        <div className="panel-heading border-0">
                            <span className="panel-title">{managePage}</span>
                        </div>

                        <div className="panel-body">
                            <div className="row">
                                {/* Start Date */}
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="start_date" className="control-label">
                                            Start Date <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
                                            id="start_date"
                                            name="start_date"
                                            value={formData.start_date}
                                            onChange={handleInputChange}
                                            placeholder="Enter Start Date"
                                        />
                                        {errors.start_date && (
                                            <div className="invalid-feedback d-block">
                                                {errors.start_date}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* End Date */}
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="end_date" className="control-label">
                                            End Date <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className={`form-control ${errors.end_date ? 'is-invalid' : ''}`}
                                            id="end_date"
                                            name="end_date"
                                            value={formData.end_date}
                                            onChange={handleInputChange}
                                            placeholder="Enter End Date"
                                        />
                                        {errors.end_date && (
                                            <div className="invalid-feedback d-block">
                                                {errors.end_date}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Lead Source Dropdown */}
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="lead_source" className="control-label">
                                            Lead Source <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-control ${errors.lead_source ? 'is-invalid' : ''}`}
                                            id="lead_source"
                                            name="lead_source"
                                            value={formData.lead_source}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select Lead Source</option>
                                            {leadSourceOptions.map((option, index) => (
                                                <option key={index} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.lead_source && (
                                            <div className="invalid-feedback d-block">
                                                {errors.lead_source}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Cost */}
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="cost" className="control-label">
                                            Cost <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.cost ? 'is-invalid' : ''}`}
                                            id="cost"
                                            name="cost"
                                            value={formData.cost}
                                            onChange={handleInputChange}
                                            placeholder="Enter Cost"
                                            min="0"
                                            step="0.01"
                                        />
                                        {errors.cost && (
                                            <div className="invalid-feedback d-block">
                                                {errors.cost}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <button
                                            type="submit"
                                            className="btn btn-primary me-2"
                                            disabled={loading}
                                        >
                                            {loading ? <LoadingSpinner /> : "Save"}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-default"
                                            onClick={handleCancel}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
                <i className="ti-arrow-up"></i>
            </a>
        </div>
    );
};

export default LeadSourceCostForm;